import { useCallback } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";

import { type SurveyConfigVersion } from "@neuromotion-inc/mightier-survey-engine";
import { SurveyEngineIface } from "./useSurveyEngine";

/**
 *
 */

interface SurveyState {
  surveyId: string;
  version: SurveyConfigVersion;
  surveyEngineJSONRepresentationStr: string; // config, context, responses
}

interface SurveyStateStore {
  saveSurveyState: (engine: SurveyEngineIface) => void;
  surveyStates: SurveyState[];
}

// Returns object including an array of surveyStates, function to save surveyState
// exported for DEBUG ONLY don't use it in real code lol
export const _useSurveyStateZustandStore = create<SurveyStateStore>()(
  persist(
    (set) => {
      return {
        surveyStates: [],
        saveSurveyState: (engine: SurveyEngineIface): void => {
          set((currentState) => {
            // Remove any existing state for this surveyId and version
            const otherStates = currentState.surveyStates.filter(
              (surveyState: SurveyState) => {
                return (
                  surveyState.surveyId !== engine.surveyConfigId &&
                  surveyState.version !== engine.surveyConfigVersion
                );
              },
            );

            // NOTE: order does not matter here (since we filter the array for lookup)
            // so we can just filter out and append the new one at the end
            // Adds new survey state for this surveyId and version

            otherStates.push({
              surveyEngineJSONRepresentationStr: engine.toJsonRepresentation(),
              surveyId: engine.surveyConfigId,
              version: engine.surveyConfigVersion,
            });

            return {
              ...currentState,
              surveyStates: otherStates,
            };
          });
        },
      };
    },
    {
      name: "survey-engine-state", // Store's key
    },
  ),
);

/**
 *
 */

type SurveyStateStoreReal = Pick<SurveyStateStore, "saveSurveyState"> & {
  getSurveyState: (
    surveyId: string,
    version: SurveyConfigVersion,
  ) => SurveyState | undefined;
  deleteSurveyState: (surveyId: string, version: SurveyConfigVersion) => void;
};

export const useSurveyStateStore = (): SurveyStateStoreReal => {
  const { saveSurveyState, surveyStates } = _useSurveyStateZustandStore();

  // Function finds the desired survey state in store of all states based on survey ID and version
  const getSurveyState = useCallback(
    (
      surveyId: string,
      version: SurveyConfigVersion,
    ): SurveyState | undefined => {
      return surveyStates.find((surveyState: SurveyState) => {
        return (
          surveyState.surveyId === surveyId && surveyState.version === version
        );
      });
    },
    [surveyStates],
  );

  const deleteSurveyState = useCallback(
    (surveyId: string, version: SurveyConfigVersion) => {
      _useSurveyStateZustandStore.setState((currentState) => {
        return {
          surveyStates: currentState.surveyStates.filter(
            (surveyState: SurveyState) => {
              return (
                surveyState.surveyId !== surveyId &&
                surveyState.version !== version
              );
            },
          ),
        };
      });
    },
    [],
  );

  return { saveSurveyState, getSurveyState, deleteSurveyState };
};
